import React, { useEffect, useRef, useState } from 'react';
import { useTabs } from '../context/TabsContext';

const FloatingCompareTab = ({ onClick, disabled = false, isCompareMode = false, isSelectionModalOpen = false, onClose }) => {
    // Collapse functionality removed - tabs always visible
    // Drag and position state
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(() => {
        // Load saved position from localStorage
        try {
            const saved = localStorage.getItem('compareTabPosition');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === 'object' && 
                    (parsed.edge === 'right' || parsed.edge === 'bottom') &&
                    typeof parsed.offset === 'number' && 
                    parsed.offset >= 0) {
                    
                    if (typeof window !== 'undefined') {
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        const buttonSize = 88;
                        
                        if (parsed.edge === 'right') {
                            const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        } else {
                            const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        }
                    }
                    return parsed;
                }
            }
        } catch (e) {
            console.warn('Failed to parse compareTabPosition from localStorage:', e);
        }
        // Default position: right edge, 30% from top
        return { 
            edge: 'right', 
            offset: typeof window !== 'undefined' 
                ? Math.max(24, Math.round(window.innerHeight * 0.3)) 
                : 24 
        };
    });
    
    // Use refs to avoid stale closures
    const positionRef = useRef(position);
    const dragStartRef = useRef(null);
    const buttonRef = useRef(null);
    const isDraggingRef = useRef(false);
    const isCompareModeRef = useRef(isCompareMode);
    const onCloseRef = useRef(onClose);
    const onClickRef = useRef(onClick);
    
    // Keep refs in sync with state and props
    useEffect(() => {
        positionRef.current = position;
    }, [position]);
    
    useEffect(() => {
        isDraggingRef.current = isDragging;
    }, [isDragging]);
    
    useEffect(() => {
        isCompareModeRef.current = isCompareMode;
    }, [isCompareMode]);
    
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);
    
    useEffect(() => {
        onClickRef.current = onClick;
    }, [onClick]);

    // Save position to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('compareTabPosition', JSON.stringify(position));
    }, [position]);

    // Validate and clamp position when window resizes
    useEffect(() => {
        const validatePosition = () => {
            if (typeof window === 'undefined') return;
            
            setPosition(currentPosition => {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                
                let needsUpdate = false;
                let newPosition = { ...currentPosition };
                
                if (currentPosition.edge === 'right') {
                    const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                } else {
                    const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                }
                
                return needsUpdate ? newPosition : currentPosition;
            });
        };
        
        validatePosition();
        window.addEventListener('resize', validatePosition);
        
        return () => {
            window.removeEventListener('resize', validatePosition);
        };
    }, []);

    const getDynamicStyle = () => {
        const bodyPadding = 'var(--spacing-sm)';
        const buttonSize = 88; // Always full size
        
        let safeOffset = position.offset;
        if (typeof window !== 'undefined') {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            if (position.edge === 'right') {
                const maxOffset = Math.max(24, viewportHeight - 88 - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            } else {
                const maxOffset = Math.max(24, viewportWidth - 88 - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            }
        }
        
        if (position.edge === 'right') {
            return {
                right: `calc(0px - ${bodyPadding})`,
                top: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9999',
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        } else {
            return {
                bottom: `calc(0px - ${bodyPadding})`,
                left: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9999',
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        }
    };

    const handleMouseDown = (e) => {
        if (e.button !== 0) return;
        
        // Reset dragging state and set up drag start
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            startPos: { ...positionRef.current },
            hasMoved: false
        };
        e.preventDefault();
    };

    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        
        // Reset dragging state and set up drag start
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            startPos: { ...positionRef.current },
            hasMoved: false
        };
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!dragStartRef.current) return;
        
        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;
        const moveThreshold = 10;
        // Collapse functionality removed
        
        // If moved beyond threshold, mark as moved and start dragging
        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            if (dragStartRef.current) {
                dragStartRef.current.hasMoved = true;
                
                if (!isDraggingRef.current) {
                    setIsDragging(true);
                    isDraggingRef.current = true;
                }
                
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                const margin = 24;
                
                let newPosition = { ...startPos };
                
                // Confine to right edge only
                newPosition.edge = 'right';
                newPosition.offset = Math.max(margin, Math.min(viewportHeight - buttonSize - margin, e.clientY - buttonSize / 2));
                
                setPosition(newPosition);
            }
        }
    };

    const handleTouchMove = (e) => {
        if (!dragStartRef.current) return;
        
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStartRef.current.x;
        const deltaY = touch.clientY - dragStartRef.current.y;
        const moveThreshold = 10;
        // Collapse functionality removed
        
        // If moved beyond threshold, mark as moved and start dragging
        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            if (dragStartRef.current) {
                dragStartRef.current.hasMoved = true;
                
                if (!isDraggingRef.current) {
                    setIsDragging(true);
                    isDraggingRef.current = true;
                }
                
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                const margin = 24;
                
                let newPosition = { ...startPos };
                
                // Confine to right edge only
                newPosition.edge = 'right';
                newPosition.offset = Math.max(margin, Math.min(viewportHeight - buttonSize - margin, touch.clientY - buttonSize / 2));
                
                setPosition(newPosition);
            }
        }
    };

    const handleMouseUp = (e) => {
        // Check if this was a click (no drag) BEFORE resetting state
        let shouldClick = false;
        if (dragStartRef.current) {
            const hasMoved = dragStartRef.current.hasMoved || false;
            const deltaX = e.clientX - dragStartRef.current.x;
            const deltaY = e.clientY - dragStartRef.current.y;
            const moveThreshold = 10;
            
            // Only trigger click if we didn't drag
            // Be more lenient when compare mode is active (for closing with X)
            const isCompareMode = isCompareModeRef.current;
            if (isCompareMode) {
                // When compare mode is active, allow clicks with slightly more movement tolerance
                shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold * 1.5 && Math.abs(deltaY) <= moveThreshold * 1.5;
            } else {
                shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold;
            }
        }
        
        // Always reset state immediately
        setIsDragging(false);
        isDraggingRef.current = false;
        const dragStart = dragStartRef.current;
        dragStartRef.current = null;
        
        // Handle click after state is cleared
        if (shouldClick) {
            // Collapse functionality removed - always handle clicks
            if (isCompareModeRef.current && onCloseRef.current) {
                onCloseRef.current();
            } else if (onClickRef.current) {
                onClickRef.current(e);
            }
        }
    };

    const handleTouchEnd = (e) => {
        // Check if this was a click (no drag) BEFORE resetting state
        let shouldClick = false;
        if (dragStartRef.current && e.changedTouches[0]) {
            const hasMoved = dragStartRef.current.hasMoved || false;
            const touch = e.changedTouches[0];
            const deltaX = touch.clientX - dragStartRef.current.x;
            const deltaY = touch.clientY - dragStartRef.current.y;
            const moveThreshold = 10;
            
            // Only trigger click if we didn't drag
            // Be more lenient when compare mode is active (for closing with X)
            const isCompareMode = isCompareModeRef.current;
            if (isCompareMode) {
                // When compare mode is active, allow clicks with slightly more movement tolerance
                shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold * 1.5 && Math.abs(deltaY) <= moveThreshold * 1.5;
            } else {
                shouldClick = !hasMoved && Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold;
            }
        }
        
        // Always reset state immediately
        setIsDragging(false);
        isDraggingRef.current = false;
        dragStartRef.current = null;
        
        // Handle click after state is cleared
        if (shouldClick) {
            // Collapse functionality removed - always handle clicks
            if (isCompareModeRef.current && onCloseRef.current) {
                onCloseRef.current();
            } else if (onClickRef.current) {
                onClickRef.current(e);
            }
        }
    };

    // Set up event listeners - use refs to avoid stale closures
    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('touchmove', handleTouchMove);
        document.addEventListener('touchend', handleTouchEnd);
        
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, []); // Empty deps - handlers use refs which are always current

    return (
        <div
            ref={buttonRef}
            className="fixed z-[9999] floating-tab-container"
            style={getDynamicStyle()}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            data-walkthrough="compare-tab"
        >
            <div className={`bg-neutral-0 hairline transition-all duration-200 ${
                position.edge === 'right' 
                    ? `rounded-l-lg border-l border-t border-b w-20 h-16` 
                    : `rounded-t-lg border-t border-l border-r w-16 h-20`
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            style={{
                margin: '0 !important',
                padding: '0 !important',
                backgroundColor: '#FFFFFF',
                borderTop: `1px solid #0D0D0D`,
                borderRight: position.edge === 'right' ? 'none' : '1px solid #0D0D0D',
                borderLeft: position.edge === 'right' ? `${isSelectionModalOpen ? '2px' : '1px'} solid #0D0D0D` : '1px solid #0D0D0D',
                borderBottom: position.edge === 'right' ? '1px solid #0D0D0D' : 'none',
                boxShadow: position.edge === 'right' ? `-${isSelectionModalOpen ? '3px' : '2px'} 0 ${isSelectionModalOpen ? '4px' : '2px'} rgba(0, 0, 0, ${isSelectionModalOpen ? '0.15' : '0.02'})` : '0 -1px 2px rgba(0, 0, 0, 0.02)',
                position: 'relative',
                display: 'block',
                cursor: isDragging ? 'grabbing' : 'grab',
                opacity: 1
            }}>
                <div className="w-full h-full flex items-center justify-center overflow-hidden p-1">
                    {isCompareMode ? (
                        <span className="text-neutral-900 font-mono text-xl leading-none" style={{ fontSize: '1.25rem' }}>
                            ✕
                        </span>
                    ) : (
                        <img 
                            src="/images/compare.png" 
                            alt="Compare Recipes"
                            className="w-full h-full object-contain"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default FloatingCompareTab;

